import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/horizonbg-logo.svg"
                  alt="HorizonBG"
                  className="h-8 w-auto"
                />
                <span className="text-xl font-semibold text-white">GP Ads AI</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/signin"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="bg-gp-sungold text-black hover:bg-gp-sungold-400 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gray-50 border-b border-gray-200 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500">Last Updated: March 16, 2026</p>
          <p className="text-gray-500">Effective Date: March 16, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-gray max-w-none">

          {/* Agreement */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you
              ("you," "your," or "User") and Good Power LLC ("Good Power," "we," "us," or "our")
              governing your access to and use of the GP Ads AI platform ("Platform," "Service")
              at <strong>gpads.io</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              By creating an account, accessing, or using the Platform, you agree to be bound by these Terms
              and our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
              If you are using the Platform on behalf of an organization, you represent that you have authority
              to bind that organization to these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you do not agree to these Terms, you must not access or use the Platform.
            </p>
          </div>

          {/* Service Description */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              GP Ads AI is a multi-tenant, AI-powered advertising management platform that provides:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Cross-Platform Campaign Management:</strong> Unified dashboard for managing advertising campaigns across Meta (Facebook/Instagram), Google Ads, and TikTok.</li>
              <li><strong>Performance Analytics:</strong> Automated data collection, normalization, and visualization of campaign metrics, demographics, and trends.</li>
              <li><strong>AI-Powered Optimization:</strong> Intelligent recommendations for ad copy, budget allocation, audience targeting, and campaign strategy.</li>
              <li><strong>Governed Actions:</strong> Approval workflows, audit trails, auto-pause rules, and action logging for compliance and accountability.</li>
              <li><strong>Creative Tools:</strong> AI Copy Studio, Creative Library, Creative Boards, and Emotional Tone Analytics for content management.</li>
              <li><strong>Team Collaboration:</strong> Role-based access control, team management, and multi-organization support.</li>
              <li><strong>API and Integrations:</strong> REST API access, Slack notifications, webhooks, and third-party integrations.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              The Platform connects to third-party advertising platforms via OAuth 2.0 authorization.
              You maintain full ownership and control of your advertising accounts and data on those platforms.
            </p>
          </div>

          {/* Account Registration */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Registration and Security</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
            <p className="text-gray-700 leading-relaxed mb-4">To use the Platform, you must:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Be at least 18 years of age</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the accuracy of your account information</li>
              <li>Have the legal authority to accept these Terms (personally or on behalf of your organization)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Security</h3>
            <p className="text-gray-700 leading-relaxed mb-4">You are responsible for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use at <a href="mailto:security@gpads.io" className="text-blue-600 hover:underline">security@gpads.io</a></li>
              <li>Ensuring that team members granted access comply with these Terms</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Organization Accounts</h3>
            <p className="text-gray-700 leading-relaxed">
              Organizations may have multiple users with different roles (viewer, analyst, manager, admin).
              The organization administrator is responsible for managing user access and permissions.
              Each organization's data is isolated from other organizations on the Platform.
            </p>
          </div>

          {/* Third-Party Platform Connections */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Platform Connections</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 OAuth Authorization</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To use the Platform's campaign management and analytics features, you must connect your
              advertising accounts from one or more supported platforms (Meta, Google Ads, TikTok)
              via OAuth 2.0 authorization. By connecting an account, you:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Authorize GP Ads AI to access your advertising data as described in our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link></li>
              <li>Confirm you have the authority to grant such access for the connected accounts</li>
              <li>Acknowledge that GP Ads AI is not responsible for the advertising platforms' terms, policies, or API availability</li>
              <li>Understand you can revoke access at any time through the Platform settings or directly through the advertising platform</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Platform Compliance</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree to comply with the terms and policies of each advertising platform you connect:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><a href="https://www.facebook.com/policies/ads/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta Advertising Policies</a></li>
              <li><a href="https://support.google.com/adspolicy/answer/6008942" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads Policies</a></li>
              <li><a href="https://ads.tiktok.com/help/article/tiktok-advertising-policies-ad-creatives-landing-page" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">TikTok Advertising Policies</a></li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              GP Ads AI does not guarantee that your campaigns comply with these platforms' advertising policies.
              You are solely responsible for ensuring your advertising content and targeting comply with
              all applicable platform policies and laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Campaign Actions</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you use GP Ads AI to manage campaigns (pause, resume, adjust budgets, etc.):
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Actions are executed through the platforms' official APIs on your behalf</li>
              <li>You are responsible for all actions taken through the Platform, including automated rules you configure (e.g., auto-pause rules)</li>
              <li>GP Ads AI provides governance tools (approval workflows, audit trails) to help you manage risk, but does not guarantee that all actions will produce the desired outcome</li>
              <li>The Platform's AI recommendations are suggestions only; you retain full decision-making authority</li>
            </ul>
          </div>

          {/* Acceptable Use */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Permitted Uses</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may use the Platform for lawful business purposes consistent with these Terms, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Managing and optimizing your advertising campaigns</li>
              <li>Analyzing campaign performance data</li>
              <li>Using AI tools to generate ad copy and creative suggestions</li>
              <li>Collaborating with team members on campaign strategies</li>
              <li>Accessing the Platform via API for authorized integrations</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Prohibited Uses</h3>
            <p className="text-gray-700 leading-relaxed mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Use the Platform for any illegal purpose or to promote illegal activities</li>
              <li>Attempt to gain unauthorized access to the Platform, other accounts, or connected systems</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Platform</li>
              <li>Scrape, crawl, or use automated tools to extract data from the Platform beyond API access</li>
              <li>Use the Platform to collect or store personal data of individuals beyond what is necessary for advertising</li>
              <li>Share your account credentials with unauthorized persons</li>
              <li>Use the Platform to create misleading, deceptive, or fraudulent advertisements</li>
              <li>Interfere with the Platform's operation, security, or infrastructure</li>
              <li>Resell, sublicense, or redistribute access to the Platform without written authorization</li>
              <li>Use AI-generated content to impersonate individuals, organizations, or government entities</li>
              <li>Circumvent rate limits, access controls, or other technical restrictions</li>
            </ul>
          </div>

          {/* Intellectual Property */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Platform Ownership</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The Platform, including all software, designs, algorithms, AI models, documentation, and branding,
              is the property of Good Power LLC and is protected by intellectual property laws.
              These Terms do not grant you any ownership rights in the Platform.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Your Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You retain ownership of all content you create or upload to the Platform, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Ad copy and creative assets</li>
              <li>Campaign configurations and settings</li>
              <li>Custom metrics and report definitions</li>
              <li>Board layouts and organizational structures</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              You grant Good Power a limited, non-exclusive license to use, display, and process your content
              solely to provide the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 AI-Generated Content</h3>
            <p className="text-gray-700 leading-relaxed">
              Content generated by the Platform's AI tools (ad copy suggestions, recommendations, etc.)
              is provided for your use in advertising. You may use, modify, and publish AI-generated content.
              However, AI-generated content is provided "as is" without guarantees of originality, accuracy,
              or compliance with any advertising platform's policies. You are responsible for reviewing and
              approving all content before publishing.
            </p>
          </div>

          {/* Data and Privacy */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data, Privacy, and Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our collection, use, and protection of your data is governed by our{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>,
              which is incorporated into these Terms by reference. Key commitments:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Data Isolation:</strong> Your organization's data is strictly separated from other customers.</li>
              <li><strong>Encryption:</strong> All OAuth tokens are encrypted at rest; all data in transit uses TLS 1.2+.</li>
              <li><strong>No Data Sales:</strong> We never sell, rent, or trade your advertising data.</li>
              <li><strong>Data Deletion:</strong> You can request complete data deletion at any time.</li>
              <li><strong>Compliance:</strong> We comply with GDPR, CCPA, and the data policies of Meta, Google, and TikTok.</li>
            </ul>
          </div>

          {/* Fees and Payment */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Fees and Payment</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Pricing</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Access to the Platform is provided on a subscription basis. Current pricing and plan details
              are available at <a href="https://gpads.io" className="text-blue-600 hover:underline">gpads.io</a>.
              We reserve the right to change pricing with 30 days' written notice.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Free Trial</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              New accounts may be eligible for a free trial period. During the trial, you have access to
              all Platform features. At the end of the trial, you must subscribe to continue using the Service.
              No payment is required during the trial, and you can cancel at any time.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.3 Billing</h3>
            <p className="text-gray-700 leading-relaxed">
              Subscriptions are billed in advance on a monthly or annual basis. All fees are non-refundable
              except where required by law. If payment fails, we may suspend access to the Platform until
              payment is received.
            </p>
          </div>

          {/* Availability and SLA */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Service Availability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We strive to maintain high availability of the Platform. However:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>The Platform may be temporarily unavailable for maintenance, updates, or due to circumstances beyond our control.</li>
              <li>Third-party advertising platform APIs may experience outages or rate limiting that affect data collection and campaign management features.</li>
              <li>AI features depend on underlying model availability and may have variable response times.</li>
              <li>We will make reasonable efforts to provide advance notice of planned maintenance.</li>
              <li>We do not guarantee specific uptime percentages unless specified in a separate Service Level Agreement (SLA).</li>
            </ul>
          </div>

          {/* Disclaimers */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Disclaimers</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</strong>
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Without limiting the foregoing, we do not warrant that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>The Platform will meet all of your requirements</li>
                <li>The Platform will be uninterrupted, timely, secure, or error-free</li>
                <li>AI-generated recommendations will produce specific advertising results</li>
                <li>Data collected from advertising platforms will be complete or error-free (as it depends on third-party APIs)</li>
                <li>Any particular level of advertising performance, ROAS, or conversion rates will be achieved</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Limitation of Liability</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, GOOD POWER LLC SHALL NOT BE LIABLE FOR ANY
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Loss of advertising revenue or campaign performance</li>
                <li>Loss of data or data breaches at third-party advertising platforms</li>
                <li>Costs of procuring substitute services</li>
                <li>Business interruption or loss of goodwill</li>
                <li>Actions taken or not taken based on AI recommendations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                <strong>Our total liability for any claims arising from your use of the Platform shall not
                exceed the amount you paid to Good Power in the 12 months preceding the claim.</strong>
              </p>
            </div>
          </div>

          {/* Indemnification */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless Good Power LLC, its officers, directors, employees,
              and agents from any claims, damages, losses, or expenses (including reasonable attorney fees)
              arising from: (a) your use of the Platform; (b) your violation of these Terms; (c) your
              advertising content or campaigns; (d) your violation of any third-party advertising platform's
              policies; or (e) your violation of any applicable law or regulation.
            </p>
          </div>

          {/* Termination */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Termination</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.1 By You</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may terminate your account at any time by contacting{' '}
              <a href="mailto:support@gpads.io" className="text-blue-600 hover:underline">support@gpads.io</a>{' '}
              or through the account settings. Upon termination:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Your access to the Platform will be revoked</li>
              <li>All connected advertising accounts will be disconnected</li>
              <li>OAuth tokens will be deleted</li>
              <li>You may request deletion of all your data per our Privacy Policy</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">13.2 By Us</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may suspend or terminate your account if:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>You violate these Terms</li>
              <li>You fail to pay applicable fees</li>
              <li>Your use poses a security risk to the Platform or other users</li>
              <li>Required by law or a third-party platform's policies</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We will provide reasonable notice before termination except in cases of security threats
              or legal requirements.
            </p>
          </div>

          {/* Governing Law */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Governing Law and Disputes</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms are governed by the laws of the State of Delaware, United States, without regard
              to conflict of law principles.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any disputes arising from these Terms or your use of the Platform shall first be resolved
              through good-faith negotiation. If negotiations fail, disputes shall be resolved through
              binding arbitration administered by the American Arbitration Association (AAA) under its
              Commercial Arbitration Rules. The arbitration shall take place in Delaware, United States.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>CLASS ACTION WAIVER:</strong> You agree that any disputes will be resolved on an
              individual basis and that you will not bring or participate in a class action, collective
              action, or representative action.
            </p>
          </div>

          {/* Changes */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these Terms from time to time. We will notify you of material changes at least
              30 days before they take effect by posting a notice on the Platform or sending an email to
              your account email address. Your continued use of the Platform after the effective date of
              revised Terms constitutes acceptance. If you do not agree to the revised Terms, you must
              stop using the Platform and terminate your account.
            </p>
          </div>

          {/* Miscellaneous */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. General Provisions</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-3">
              <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and Good Power regarding the Platform.</li>
              <li><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any provision does not constitute a waiver of that provision.</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our written consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets.</li>
              <li><strong>Force Majeure:</strong> We are not liable for failures caused by events beyond our reasonable control, including natural disasters, war, terrorism, pandemics, or internet or power outages.</li>
              <li><strong>Notices:</strong> We may send notices to the email address associated with your account. You may send notices to <a href="mailto:legal@gpads.io" className="text-blue-600 hover:underline">legal@gpads.io</a>.</li>
            </ul>
          </div>

          {/* Contact */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about these Terms of Service, contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 mb-1"><strong>Good Power LLC</strong></p>
              <p className="text-gray-700 mb-1">GP Ads AI Legal Team</p>
              <p className="text-gray-700 mb-1">Email: <a href="mailto:legal@gpads.io" className="text-blue-600 hover:underline">legal@gpads.io</a></p>
              <p className="text-gray-700 mb-1">Support: <a href="mailto:support@gpads.io" className="text-blue-600 hover:underline">support@gpads.io</a></p>
              <p className="text-gray-700">Website: <a href="https://gpads.io" className="text-blue-600 hover:underline">https://gpads.io</a></p>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src="/horizonbg-logo.svg" alt="HorizonBG" className="h-6 w-auto" />
              <span className="text-white font-semibold">GP Ads AI</span>
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link>
              <Link to="/" className="text-gray-400 hover:text-white text-sm">Home</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm">
            <p>&copy; 2026 Good Power LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
